import type { Breadcrumb, BrowserOptions } from '@sentry/browser';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { performHttpFetch } from '@atlassian/conversation-assistant-utils/httpUtils';
// import { useQuery } from '@atlassian/conversation-assistant-utils/queryUtils';

import { isClientFetchError } from '../../common/utils/is-error';
import { getRovoChatProduct } from '../../common/utils/product-config';

import { ConversationAssistantPrivateReporter } from './private-reporter';
import {
	createPageSessionReportingPipelineSelector,
	type ReportingPipeline,
} from './reporting-pipeline-selector';
import { normaliseUrl, normalizeUUID } from './utils';

// importing local file instead of from '@sentry/browser' otherwise there will be typescript errors
const importLegacySentryAsync = async () =>
	import(
		/* webpackChunkName: "@atlassian/conversation-assistant-instrumentation/sentry" */
		'@sentry/browser'
	);

const importPrivateSentryAsync = async () => {
	const sentry = await import(
		/* webpackChunkName: "@atlassian/conversation-assistant-instrumentation/private-sentry" */
		'@sentry/browser'
	);
	return {
		BrowserClient: sentry.BrowserClient,
		eventFromException: sentry.eventFromException,
	};
};

type SentryType = Awaited<ReturnType<typeof importLegacySentryAsync>>;

const DEFAULT_PACKAGE_NAME = '@atlassian/conversation-assistant';
const DEFAULT_SENTRY_DSN =
	'https://0d679a1c0fe3816e2b2e34d4ca85d5da@o55978.ingest.sentry.io/4506794008903680';
const KNOWN_NON_PRODUCTION_LOCATION_PARTS = [
	'localhost',
	'.local',
	'jira-dev.com',
	'stg.atlassian.com',
	'stg.internal.atlassian.com',
	'stg-east.frontend.public.atl-paas.net',
];
type SentrySetupFailureReason = 'sentry_version_mismatch';

export type SentrySetupFailure = {
	reason: SentrySetupFailureReason;
	product: string;
	packageName: string;
	setupRetrySuppressed: boolean;
};

type SetupSentryClientOptions = {
	customSentryDsn?: string;
	packageName?: string;
	product?: string;
	ignoreErrors?: BrowserOptions['ignoreErrors'];
	packageVersion?: string;
	onSetupFailure?: (failure: SentrySetupFailure) => void;
};

let sentryClient: SentryType | undefined;
let privateReporter: ConversationAssistantPrivateReporter | undefined;
let selectedReportingPipeline: ReportingPipeline | undefined;
const reportingPipelineSelector = createPageSessionReportingPipelineSelector(() =>
	fg('rovo_chat_sentry_isolated_reporter'),
);
let sentrySetupFailed = false;
let sentrySetupFailure: SentrySetupFailure | undefined;
// Report the irrecoverable setup failure once per page session. The underlying mismatch cannot
// recover until the host reloads with compatible Sentry versions.
let sentrySetupFailureReported = false;
let accountIdPromise: Promise<string> | undefined;

const defaultAccountIdProvider = (): Promise<string> =>
	new Promise((resolve, reject) => {
		performHttpFetch<{ account_id?: string }>({
			path: '/gateway/api/me',
			options: { method: 'GET' },
		})
			.then((profileData) => {
				if (profileData?.account_id) {
					resolve(profileData.account_id);
				} else {
					reject(new Error('Profile data not loaded'));
				}
			})
			.catch((error) => {
				reject(error);
			});
	});

let accountIdProvider: () => Promise<string> = defaultAccountIdProvider;

/**
 * Override the provider used by `setupSentryClient` to look up the current
 * user's account id. Intended for tests and example fixtures so they don't
 * have to mock the underlying `/gateway/api/me` HTTP call.
 *
 * Resets the cached account-id promise so the next `setupSentryClient` call
 * uses the new provider.
 */
export const setAccountIdProvider = (fn: () => Promise<string>): void => {
	accountIdProvider = fn;
	accountIdPromise = undefined;
};

/**
 * Restore the default provider (which fetches `/gateway/api/me`).
 */
export const resetAccountIdProvider = (): void => {
	accountIdProvider = defaultAccountIdProvider;
	accountIdPromise = undefined;
};

export const resetSentryClientForTests = (): void => {
	privateReporter?.resetForTests();
	sentryClient = undefined;
	privateReporter = undefined;
	selectedReportingPipeline = undefined;
	reportingPipelineSelector.resetForTests();
	sentrySetupFailed = false;
	sentrySetupFailure = undefined;
	sentrySetupFailureReported = false;
	accountIdPromise = undefined;
	accountIdProvider = defaultAccountIdProvider;
};

// copied from platform/packages/people-and-teams/teams-client/src/services/sentry/utils/initialise-sentry.ts
const DISABLED_INTEGRATIONS = [
	'onerror',
	'onunhandledrejection',
	// In Jira this integration is being initialised twice, resulting in no events being sent to Sentry
	'Dedupe',
];

const getEnvironment = (): BrowserOptions['environment'] => {
	if (process.env.NODE_ENV !== 'production') {
		return;
	}

	if (process.env.CLOUD_ENV === 'staging') {
		return 'staging';
	}

	return 'production';
};

const getReportingPipeline = (): ReportingPipeline => {
	if (!selectedReportingPipeline) {
		selectedReportingPipeline = reportingPipelineSelector.get();
	}
	return selectedReportingPipeline;
};

export const isIsolatedSentryReportingSelected = (): boolean =>
	getReportingPipeline() === 'isolatedPrivateV1';

const getRelease = (): string | undefined => {
	if (typeof window === 'undefined') {
		return;
	}
	return (window as Window & { SENTRY_RELEASE?: { id?: string } }).SENTRY_RELEASE?.id;
};

const setupPrivateReporter = async ({
	accountId,
	customSentryDsn,
	ignoreErrors,
	packageName,
	packageVersion,
	product,
}: {
	accountId: string;
	customSentryDsn?: string;
	ignoreErrors?: BrowserOptions['ignoreErrors'];
	packageName: string;
	packageVersion?: string;
	product: string;
}): Promise<void> => {
	const dsn = customSentryDsn || DEFAULT_SENTRY_DSN;
	if (privateReporter) {
		privateReporter.updateContext({ accountId, packageVersion, product });
		return;
	}

	try {
		const importedSentry = await importPrivateSentryAsync();
		privateReporter = new ConversationAssistantPrivateReporter({
			accountId,
			dsn,
			environment: getEnvironment(),
			ignoreErrors,
			packageName,
			packageVersion,
			product,
			release: getRelease(),
			sentry: importedSentry,
		});
	} catch {
		// Private reporting is best effort and must not affect Sentry callers.
	}
};

export const setupSentryClient = async ({
	customSentryDsn,
	packageName,
	product: productOverride,
	ignoreErrors,
	packageVersion,
	onSetupFailure,
}: SetupSentryClientOptions = {}): Promise<void> => {
	let accountId = 'UNKNOWN_AAID';
	let product = productOverride ?? getRovoChatProduct();
	const resolvedPackageName = packageName || DEFAULT_PACKAGE_NAME;

	if (getReportingPipeline() === 'legacyGlobal' && sentrySetupFailed) {
		reportSentrySetupFailure(onSetupFailure);
		return;
	}

	try {
		if (!accountIdPromise) {
			accountIdPromise = accountIdProvider();
		}
		accountId = await accountIdPromise;
	} catch {
		// do nothing
	}

	if (isIsolatedSentryReportingSelected()) {
		await setupPrivateReporter({
			accountId,
			customSentryDsn,
			ignoreErrors,
			packageName: resolvedPackageName,
			packageVersion,
			product,
		});
		return;
	}

	if (sentryClient) {
		// if sentry is trying to be initialized again with new product or account id
		// update the user and tags
		sentryClient.setTag('product', product);
		sentryClient.setUser({ id: accountId });
		// do not initialise sentry again, only update the existing one
		return;
	}

	let importedSentry;
	try {
		importedSentry = await importLegacySentryAsync();
	} catch (e) {
		console.warn('Failed to load conversation-assistant sentry', e); // eslint-disable-line no-console
		return;
	}

	const shouldSuppressSetupRetry = fg('rovo_chat_sentry_setup_retry_killswitch');

	try {
		importedSentry.init({
			dsn: customSentryDsn || DEFAULT_SENTRY_DSN,
			environment: getEnvironment(),
			integrations: (integrations) => {
				return integrations.filter((integration) => {
					return DISABLED_INTEGRATIONS.indexOf(integration.name) === -1;
				});
			},
			initialScope: {
				user: {
					id: accountId,
				},
				tags: {
					packageName: resolvedPackageName,
					product,
					packageVersion,
				},
			},
			// referencing jira/src/packages/platform/observability/errors-handling/src/utils/sentry/handling/normalise-sentry-event.tsx
			beforeSend: (event, hint) => {
				const exception = hint?.originalException;
				// Ignore network-like errors
				if (isClientFetchError(exception)) {
					return null;
				}

				if (!event.tags?.name) {
					// we only want to capture errors that has `name` tag from `logException` function below
					// since the `DISABLED_INTEGRATIONS` filter above is not enough to prevent unhandled errors
					return null;
				}

				let updatedEvent = event;
				if (updatedEvent.request?.url) {
					// Ignore events from localhost and staging
					if (
						KNOWN_NON_PRODUCTION_LOCATION_PARTS.some((part) =>
							updatedEvent.request?.url?.includes(part),
						)
					) {
						return null;
					}

					updatedEvent.request.url = normaliseUrl(updatedEvent.request.url);
				}

				if (updatedEvent.exception?.values) {
					const allMatchedUUIDs: string[] = [];

					updatedEvent.exception.values = updatedEvent.exception.values.map((exception) => {
						const { normalized, matchedUUIDs } = normalizeUUID(exception.value);
						allMatchedUUIDs.push(...matchedUUIDs);
						return {
							...exception,
							value: normalized,
						};
					});

					if (allMatchedUUIDs.length > 0) {
						if (!updatedEvent.tags) {
							updatedEvent.tags = {};
						}
						updatedEvent.tags.matchedUUID = JSON.stringify(allMatchedUUIDs);
					}
				}

				return updatedEvent;
			},
			attachStacktrace: true,
			ignoreErrors,
		});

		sentryClient = importedSentry;
	} catch (error) {
		// this function throws e.update is not a function errors when we have a mismatch in sentry versions.
		// all targeted errors include 'update is not a function' even if minified differently
		if (error instanceof Error && error.message.includes('update is not a function')) {
			// Instead of throwing and logging the error to sentry, swallow it.
			if (shouldSuppressSetupRetry) {
				sentrySetupFailure = {
					reason: 'sentry_version_mismatch',
					product,
					packageName: resolvedPackageName,
					setupRetrySuppressed: true,
				};
				reportSentrySetupFailure(onSetupFailure);
				sentrySetupFailed = true;
			} else {
				sentryClient = importedSentry;
			}
		} else {
			throw error;
		}
	}
};

const reportSentrySetupFailure = (
	onSetupFailure: SetupSentryClientOptions['onSetupFailure'],
): void => {
	if (!onSetupFailure || !sentrySetupFailure || sentrySetupFailureReported) {
		return;
	}

	sentrySetupFailureReported = true;
	try {
		onSetupFailure(sentrySetupFailure);
	} catch {
		// Setup failure telemetry is best effort and must not break Sentry callers.
	}
};

const getSentryClient = async () => {
	if (!sentryClient && !sentrySetupFailed) {
		// this only happens if something using sentry before the initialisation in root app
		await setupSentryClient();
	}

	return sentryClient;
};

type Tags = {
	[key: string]: string | undefined;
	teamName?: string;
};

export const logException = async ({
	exception,
	name,
	tags,
}: {
	exception: Error | unknown;
	name: string;
	tags?: Tags;
}): Promise<void> => {
	if (isIsolatedSentryReportingSelected()) {
		if (!privateReporter) {
			await setupSentryClient();
		}
		try {
			await privateReporter?.captureException({ exception, name, tags });
		} catch {
			// Private reporting is best effort and must not affect Sentry callers.
		}
		return;
	}

	const sentryClient = await getSentryClient();
	if (!sentryClient || typeof sentryClient.captureException !== 'function') {
		return;
	}

	sentryClient.withScope((scope) => {
		scope.setTag('name', name);
		if (tags) {
			scope.setTags(tags);
		}

		if (tags?.source === 'aiMateExperienceTracker') {
			scope.setTag('experience', name);
		}

		try {
			sentryClient.captureException(exception, scope);
		} catch (error) {
			// this function throws e.update is not a function errors when we have a mismatch in sentry versions. Instead of throwing and logging the error to sentry, swallow it.
			// TODO: upgrade sentry across confluence, jira, etc. to the same version to avoid this error
		}
	});
};

export async function addSentryBreadcrumb(crumb: string | Breadcrumb): Promise<void> {
	if (isIsolatedSentryReportingSelected()) {
		if (!privateReporter) {
			await setupSentryClient();
		}
		try {
			privateReporter?.addBreadcrumb(crumb);
		} catch {
			// Breadcrumb collection is best effort and must never affect the caller.
		}
		return;
	}

	const sentryClient = await getSentryClient();
	if (!sentryClient || typeof sentryClient.addBreadcrumb !== 'function') {
		return;
	}
	sentryClient.addBreadcrumb(typeof crumb === 'string' ? { message: crumb } : crumb);
}

export const logSentryConsoleError = (...args: unknown[]): void => {
	console.error(...args); // eslint-disable-line no-console
	if (isIsolatedSentryReportingSelected() && privateReporter) {
		try {
			privateReporter.addBreadcrumb({
				category: 'console',
				level: 'error' as Breadcrumb['level'],
				message: args.map(String).join(' '),
			});
		} catch {
			// Mirroring selected console output is best effort.
		}
	}
};

export const logSentryConsoleWarning = (...args: unknown[]): void => {
	console.warn(...args); // eslint-disable-line no-console
	if (isIsolatedSentryReportingSelected() && privateReporter) {
		try {
			privateReporter.addBreadcrumb({
				category: 'console',
				level: 'warning' as Breadcrumb['level'],
				message: args.map(String).join(' '),
			});
		} catch {
			// Mirroring selected console output is best effort.
		}
	}
};
